import { createSelector } from 'reselect';
import { EMPTY_OBJECT } from './constants';
import { createCustomGroupsFromCustomGroupRanges } from './util';
import { selectReadings } from '../../../../selectors/alignedReadings';
import { selectCustomerRemediationActionTypes } from '../../../../selectors/customer';
import { getSeriesKey } from '../../../../../components/BelowCriterionGroupSelector/util/chart';

const selectChartRemediations = (stateApp = EMPTY_OBJECT, chartId) => {
	const { chartsRemediations = EMPTY_OBJECT } = stateApp;
	const chartRemediations = chartsRemediations[chartId] || EMPTY_OBJECT;
	return chartRemediations;
};

export const selectCustomGroupRanges = (stateApp = EMPTY_OBJECT, chartId) => {
	const chartRemediations = selectChartRemediations(stateApp, chartId);
	const { customGroupRanges } = chartRemediations;
	return customGroupRanges;
};

export const selectHasCustomRemediationGroups = (stateApp, chartId) => {
	const customGroupRanges = selectCustomGroupRanges(stateApp, chartId);
	return customGroupRanges && customGroupRanges.length > 0;
};

export const selectComputedBcMap = (stateApp = EMPTY_OBJECT, chartId) => {
	const chartRemediations = selectChartRemediations(stateApp, chartId);
	const { computedBcMap } = chartRemediations;
	return computedBcMap;
};

export const selectCustomExceptions = (stateApp = EMPTY_OBJECT, chartId) => {
	const chartRemediations = selectChartRemediations(stateApp, chartId);
	const { customExceptions } = chartRemediations;
	return customExceptions;
};

export const selectCustomExceptionsMap = (stateApp = EMPTY_OBJECT, chartId) => {
	const chartRemediations = selectChartRemediations(stateApp, chartId);
	const { customExceptionsMap } = chartRemediations;
	return customExceptionsMap;
};

export const selectCustomExceptionsWithGaps = (
	stateApp = EMPTY_OBJECT,
	chartId
) => {
	const chartRemediations = selectChartRemediations(stateApp, chartId);
	const { customExceptionsWithGaps } = chartRemediations;
	return customExceptionsWithGaps;
};

export const selectRemediationGroupRanges = (
	stateApp = EMPTY_OBJECT,
	chartId
) => {
	const chartRemediations = selectChartRemediations(stateApp, chartId);
	const { remediationGroupRanges } = chartRemediations;
	return remediationGroupRanges;
};

export const selectRemediationReadings = (stateApp = EMPTY_OBJECT, chartId) => {
	const chartRemediations = selectChartRemediations(stateApp, chartId);
	const { remediationReadings } = chartRemediations;
	return remediationReadings;
};

const helpSelectReadings = (stateApp, { survey }) => {
	return selectReadings(survey, stateApp);
};

const helpSelectCustomRangeGroups = (stateApp, { chartId }) => {
	return selectCustomGroupRanges(stateApp, chartId);
};

export const createSelectCustomGroups = () => {
	return createSelector(
		[helpSelectCustomRangeGroups, helpSelectReadings],
		createCustomGroupsFromCustomGroupRanges
	);
};

export const selectRemediationActions = (stateApp, chartId) => {
	const chartRemediations = selectChartRemediations(stateApp, chartId) || {};
	const { remediationActions } = chartRemediations;

	return remediationActions || EMPTY_OBJECT;
};

export const selectRemediationActionsByGroup = (stateApp, chartId) => {
	const remediationGroupRanges = selectRemediationGroupRanges(
		stateApp,
		chartId
	);
	const remediationActions = selectRemediationActions(stateApp, chartId);
	const customerRemediationActionTypes = selectCustomerRemediationActionTypes(
		stateApp
	);

	// @todo @WBFIX ---- adding null check here --- needs to be reviewed for potential hidden bugs
	const result = (remediationGroupRanges || []).reduce((accRoot, group) => {
		const groupKey = getSeriesKey({ dataSource: group });
		const actionsForGroup = remediationActions[groupKey] || {};

		// Determine which action types for this group exist
		const actionTypeKeysForGroupMap = Object.keys(actionsForGroup).reduce(
			(acc, actionKey) => {
				const actionType = actionsForGroup[actionKey].type;
				acc[actionType] = true;
				return acc;
			},
			{}
		);

		// Build ordered list of actionTypes
		const actionTypeKeysForGroup = customerRemediationActionTypes.filter(
			type => !!actionTypeKeysForGroupMap[type]
		);

		// Group actions by action type
		const actionTypes = actionTypeKeysForGroup.map(actionTypeKey => {
			return {
				actionType: actionTypeKey,
				actions: Object.keys(actionsForGroup).reduce((acc, actionKey) => {
					const action = actionsForGroup[actionKey];
					if (action.type === actionTypeKey) {
						acc.push({
							...action,
							key: actionKey
						});
					}
					return acc;
				}, [])
			};
		});

		// eslint-disable-next-line no-param-reassign
		accRoot[groupKey] = {
			groupKey,
			actionTypes
		};

		return accRoot;
	}, {});

	return result;
};

const createRemediationGroupsByKey = (remediationGroupRanges = []) =>
	remediationGroupRanges.reduce((acc, group) => {
		const key = getSeriesKey({ dataSource: group });
		acc[key] = {
			key,
			group
		};

		return acc;
	}, {});

export const selectRemediationGroupsByActionType = (stateApp, chartId) => {
	const remediationGroupRanges = selectCustomGroupRanges(stateApp, chartId);
	const remediationGroupsByKey = createRemediationGroupsByKey(
		remediationGroupRanges
	);
	const remediationGroupKeys = Object.keys(remediationGroupsByKey);
	const remediationActions = selectRemediationActions(stateApp, chartId);

	// @todo - this doesn't appear to be in use - re-evaluate and remove if not used
	// inject actions into group meta objects
	// format - { key, group, actions }
	remediationGroupKeys.forEach(key => {
		const meta = remediationGroupsByKey[key];
		meta.actions = remediationActions[key];
	});

	// initialize remediationGroupsByActionType
	const RemediationActionTypes = selectCustomerRemediationActionTypes(stateApp);
	const remediationGroupsByActionType = RemediationActionTypes.reduce(
		(acc, actionType) => {
			acc[actionType] = [];
			return acc;
		},
		{}
	);

	// inject groupMeta w/Actions into correct ActionType mapping
	remediationGroupKeys.forEach(groupKey => {
		const groupMeta = remediationGroupsByKey[groupKey];
		const { actions = {} } = groupMeta;
		const actionKeys = Object.keys(actions);

		actionKeys.forEach(actionKey => {
			const action = actions[actionKey];
			const { type } = action;
			if (type) {
				const hasGroupMapped =
					remediationGroupsByActionType[type].indexOf(groupMeta) > -1;
				if (!hasGroupMapped) {
					remediationGroupsByActionType[type].push(groupMeta);
				}
			}
		});
	});

	return remediationGroupsByActionType;
};

export const selectRemediationAction = (
	stateApp,
	chartId,
	groupKey,
	actionKey
) => {
	const remediationActions = selectRemediationActions(stateApp, chartId) || {};
	return (remediationActions[groupKey] || {})[actionKey];
};

export default {
	selectCustomGroupRanges,
	selectHasCustomRemediationGroups,
	selectCustomExceptions,
	selectCustomExceptionsWithGaps,
	selectRemediationGroupRanges,
	selectRemediationReadings,
	createSelectCustomGroups,
	selectRemediationActions,
	selectRemediationActionsByGroup,
	selectRemediationGroupsByActionType,
	selectRemediationAction
};
